/**
 * Reports Page - Refactored version using modular components
 * 
 * This is the main Reports page that uses components from /components/reports/
 */
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { useData } from '../context/DataContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  ArrowLeft,
  Loader2,
  CheckCircle,
  Download,
  RefreshCw,
  Users,
  BookOpen,
  Clock,
  ChevronRight,
  BarChart3,
  FileText,
  Filter,
  Search,
  FileDown,
  Database,
  AlertCircle,
  UserX,
  Calendar,
  TrendingUp,
  TrendingDown,
  UserPlus,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import jsPDF from 'jspdf';

// Import refactored components
import {
  UserCard,
  GesamtSummaryCards,
  AblaufSummaryCards,
  StatusPieChart,
  StatusBarChart,
  CourseBarChart,
  AblaufPieChart,
  AblaufBarChart,
  ExpiryLevelFilterCard,
  FilteredExpiryList,
  TeamReportTable,
  UserDetailDialog,
  GesamtReportTabs,
  AblaufReportTabs,
  KursReportSection,
  ReportActions,
  API,
  colors
} from '../components/reports';

const Reports = () => {
  const navigate = useNavigate();
  const { dataStatus, isDataLoaded, helperTeamsVersion } = useData();
  const { user, token } = useAuth();
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [reportResult, setReportResult] = useState(null);
  
  // Ref for scrolling to top of results
  const resultsTopRef = useRef(null);
  
  // Report options
  const [reportType, setReportType] = useState('gesamt');
  const [filterHelperTeams, setFilterHelperTeams] = useState(true);
  const [selectedCourse, setSelectedCourse] = useState('');  // DEPRECATED - for backward compatibility
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [courseFilterMode, setCourseFilterMode] = useState('all');
  const [coursePrefix, setCoursePrefix] = useState('');
  const [courseExcludePrefix, setCourseExcludePrefix] = useState('');
  const [showCourseDialog, setShowCourseDialog] = useState(false);
  const [courseDialogSearch, setCourseDialogSearch] = useState('');
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [teamFilterMode, setTeamFilterMode] = useState('all');
  const [teamPrefix, setTeamPrefix] = useState('');
  const [teamExcludePrefix, setTeamExcludePrefix] = useState('');
  const [showTeamDialog, setShowTeamDialog] = useState(false);
  const [teamDialogSearch, setTeamDialogSearch] = useState('');
  const [teamDialogExcludeFilter, setTeamDialogExcludeFilter] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  
  // Expiry level settings
  const [expiryL1, setExpiryL1] = useState(4);
  const [expiryL2, setExpiryL2] = useState(6);
  const [expiryL3, setExpiryL3] = useState(8);
  const [expiredA1, setExpiredA1] = useState(2);
  const [expiredA2, setExpiredA2] = useState(4);
  const [activeExpiryFilter, setActiveExpiryFilter] = useState(null);

  // Date options
  const [referenceDate, setReferenceDate] = useState('');  // Stichtag
  const [periodFromDate, setPeriodFromDate] = useState('');  // Zeitraum Von
  const [periodToDate, setPeriodToDate] = useState('');  // Zeitraum Bis

  // Exclusion list settings
  const [exclusionList, setExclusionList] = useState([]);
  const [ignoreExclusionList, setIgnoreExclusionList] = useState(false);
  const [excludedCount, setExcludedCount] = useState(0);

  // User detail dialog
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetailOpen, setUserDetailOpen] = useState(false);

  // Fetch exclusion list on mount
  useEffect(() => {
    const fetchExclusionList = async () => {
      if (!token) return;
      try {
        const response = await axios.get(`${API}/exclusion-list/emails`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setExclusionList(response.data || []);
      } catch (error) {
        console.error('Error fetching exclusion list:', error);
        setExclusionList([]);
      }
    };
    fetchExclusionList();
  }, [token]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(userSearch), 300);
    return () => clearTimeout(timer);
  }, [userSearch]);

  // Reset filters when report type changes
  useEffect(() => {
    setTeamFilterMode('all');
    setSelectedTeams([]);
    setTeamPrefix('');
    setTeamExcludePrefix('');
    setCourseFilterMode('all');
    setSelectedCourses([]);
    setCoursePrefix('');
    setCourseExcludePrefix('');
  }, [reportType]);

  // Auto-prepare reports when data is loaded
  useEffect(() => {
    if (isDataLoaded && !analysisResult && !isAnalyzing) {
      handlePrepareReports();
    }
  }, [isDataLoaded]);

  // Re-prepare reports when helper teams configuration changes
  useEffect(() => {
    if (isDataLoaded && helperTeamsVersion > 0 && !isAnalyzing) {
      handlePrepareReports();
      toast.info('Hilfsteams-Konfiguration geändert - Daten werden neu geladen');
    }
  }, [helperTeamsVersion]);

  const handlePrepareReports = async () => {
    setIsAnalyzing(true);
    try {
      const response = await axios.post(`${API}/data/prepare-reports`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalysisResult(response.data);
      toast.success('Daten erfolgreich vorbereitet');
    } catch (error) {
      console.error('Prepare error:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Vorbereiten der Daten');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleUserClick = async (user) => {
    const userName = user.user_name || '';
    const userEmail = user.user_email || '';
    
    setSelectedUser({
      name: userName || userEmail || 'Unbekannt',
      email: userEmail,
      team: user.team || user.teams?.join(', '),
      courses: [],
      loading: true
    });
    setUserDetailOpen(true);
    
    try {
      const params = new URLSearchParams();
      if (userName) params.append('user_name', userName);
      if (userEmail) params.append('user_email', userEmail);
      
      const response = await axios.get(`${API}/data/user-courses?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSelectedUser({
        name: userName || userEmail || 'Unbekannt',
        email: userEmail,
        team: user.team || user.teams?.join(', '),
        courses: response.data.courses || [],
        loading: false,
        total_courses: response.data.total_courses,
        summary: response.data.summary
      });
    } catch (error) {
      console.error('Error fetching user courses:', error);
      const fallbackCourses = user.course_name ? [{
        course_name: user.course_name,
        status: user.success === 'true' || user.success === true ? 'completed' : 'not_completed',
        completion_date: user.completion_date || '',
        invited_date: user.invited_date || '',
        expiry_date: user.expiry_date || '',
        success: user.success || ''
      }] : [];
      
      setSelectedUser({
        name: userName || userEmail || 'Unbekannt',
        email: userEmail,
        team: user.team || user.teams?.join(', '),
        courses: fallbackCourses,
        loading: false,
        error: 'Fehler beim Laden der Kursdaten'
      });
    }
  };

  // Computed: Filter data based on active expiry level filter
  const filteredByExpiryLevel = useMemo(() => {
    if (!reportResult || !activeExpiryFilter) return null;
    
    if (activeExpiryFilter.startsWith('L')) {
      return reportResult.expiring_soon_lists?.[activeExpiryFilter] || [];
    } else if (activeExpiryFilter.startsWith('A')) {
      return reportResult.expired_level_lists?.[activeExpiryFilter] || [];
    }
    return null;
  }, [reportResult, activeExpiryFilter]);

  const handleGenerateReport = async () => {
    if (!analysisResult?.session_id) {
      toast.error('Bitte zuerst Daten vorbereiten');
      return;
    }
    
    // Validate Zeitraum dates
    if (reportType === 'zeitraum') {
      if (!periodFromDate || !periodToDate) {
        toast.error('Bitte Von- und Bis-Datum für den Zeitraum-Report angeben');
        return;
      }
      if (new Date(periodFromDate) > new Date(periodToDate)) {
        toast.error('Von-Datum muss vor Bis-Datum liegen');
        return;
      }
    }
    
    setIsGenerating(true);
    setActiveExpiryFilter(null);
    
    try {
      const params = new URLSearchParams({
        session_id: analysisResult.session_id,
        report_type: reportType,
        filter_helper_teams: filterHelperTeams.toString()
      });
      
      // Course filter - new multi-select mode
      if (courseFilterMode === 'selected' && selectedCourses.length > 0) {
        params.append('selected_courses', selectedCourses.join(','));
      } else if (courseFilterMode === 'prefix') {
        if (coursePrefix.trim()) {
          params.append('course_prefix', coursePrefix.trim());
        }
        if (courseExcludePrefix.trim()) {
          params.append('course_exclude_prefix', courseExcludePrefix.trim());
        }
      }
      
      // Team filter
      if (teamFilterMode === 'selected' && selectedTeams.length > 0) {
        params.append('selected_teams', selectedTeams.join(','));
      } else if (teamFilterMode === 'prefix') {
        if (teamPrefix.trim()) {
          params.append('team_prefix', teamPrefix.trim());
        }
        if (teamExcludePrefix.trim()) {
          params.append('team_exclude_prefix', teamExcludePrefix.trim());
        }
      }
      
      params.append('expiry_l1', expiryL1.toString());
      params.append('expiry_l2', expiryL2.toString());
      params.append('expiry_l3', expiryL3.toString());
      params.append('expired_a1', expiredA1.toString());
      params.append('expired_a2', expiredA2.toString());
      
      // Add reference date (Stichtag) if set
      if (referenceDate) {
        params.append('reference_date', referenceDate);
      }
      
      // Add period dates for Zeitraum report
      if (reportType === 'zeitraum' && periodFromDate && periodToDate) {
        params.append('from_date', periodFromDate);
        params.append('to_date', periodToDate);
      }
      
      if (!ignoreExclusionList && exclusionList.length > 0) {
        params.append('exclude_emails', exclusionList.join(','));
      }
      
      const response = await axios.post(`${API}/services/reports/generate?${params.toString()}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setReportResult(response.data);
      setExcludedCount(response.data.excluded_count || 0);
      
      // Scroll to top of results after DOM update
      setTimeout(() => {
        if (resultsTopRef.current) {
          resultsTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 150);
      
      toast.success('Report erfolgreich erstellt');
    } catch (error) {
      console.error('Report error:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen des Reports');
    } finally {
      setIsGenerating(false);
    }
  };

  const resetAll = () => {
    setAnalysisResult(null);
    setReportResult(null);
    setSelectedCourse('');
    setSelectedCourses([]);
    setCourseFilterMode('all');
    setCoursePrefix('');
    setCourseExcludePrefix('');
    setSelectedTeams([]);
    setTeamFilterMode('all');
    setTeamPrefix('');
    setTeamExcludePrefix('');
    setActiveExpiryFilter(null);
    setReferenceDate('');
    setPeriodFromDate('');
    setPeriodToDate('');
  };

  const backToOptions = () => {
    setReportResult(null);
    setUserSearch('');
    setActiveExpiryFilter(null);
  };

  // Memoized filtered lists
  const filteredLists = useMemo(() => {
    if (!reportResult) return { notCompleted: [], expired: [], completed: [] };
    
    const search = debouncedSearch.toLowerCase().trim();
    
    const filterFn = (users) => {
      if (!search || !users) return users || [];
      return users.filter(user => 
        (user.user_name || '').toLowerCase().includes(search) ||
        (user.user_email || '').toLowerCase().includes(search)
      );
    };
    
    return {
      notCompleted: filterFn(reportResult.not_completed_list),
      expired: filterFn(reportResult.expired_list),
      completed: filterFn(reportResult.completed_list)
    };
  }, [reportResult, debouncedSearch]);

  // PDF Download handler
  const handleDownloadPDF = () => {
    if (!reportResult) return;
    
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const summary = reportResult.summary || {};
      
      pdf.setFontSize(22);
      pdf.setTextColor(13, 36, 64);
      pdf.text('Report Zusammenfassung', pageWidth / 2, 25, { align: 'center' });
      
      pdf.setFontSize(11);
      pdf.setTextColor(90, 122, 155);
      pdf.text(`Erstellt am: ${new Date().toLocaleDateString('de-DE')}`, pageWidth / 2, 35, { align: 'center' });
      
      const userName = user?.name || user?.email || 'Unbekannt';
      pdf.text(`Erstellt von: ${userName}`, pageWidth / 2, 42, { align: 'center' });
      
      const reportTypeNames = {
        'gesamt': 'Gesamtübersicht',
        'team': 'Nach Team/Abteilung',
        'kurs': 'Nach Kurs',
        'ablauf': 'Ablaufdatum'
      };
      pdf.text(`Report-Typ: ${reportTypeNames[reportResult.report_type] || reportResult.report_type}`, pageWidth / 2, 49, { align: 'center' });
      
      pdf.setDrawColor(200, 216, 232);
      pdf.line(20, 55, pageWidth - 20, 55);
      
      let yPos = 70;
      
      const checkPageBreak = (requiredSpace = 20) => {
        if (yPos > 270 - requiredSpace) {
          pdf.addPage();
          yPos = 20;
        }
      };
      
      // Statistics
      pdf.setFontSize(16);
      pdf.setTextColor(13, 36, 64);
      pdf.text('Statistik', 20, yPos);
      yPos += 12;
      
      // Calculate actual counts from the filtered data
      // For Ablauf report
      let actualCompleted = 0;
      let actualExpired = 0;
      let actualOpen = 0;
      let actualTotal = 0;
      
      if (reportResult.report_type === 'zeitraum') {
        // For Zeitraum report, use period-specific lists
        actualCompleted = (reportResult.completed_in_period || []).length;
        actualExpired = (reportResult.expired_in_period || []).length;
        actualOpen = (reportResult.invited_in_period || []).length;
        actualTotal = actualCompleted + actualExpired + actualOpen;
      } else if (reportResult.report_type === 'ablauf') {
        // Count from the actual lists
        actualCompleted = (reportResult.valid_list || []).length;
        actualOpen = (reportResult.open_list || []).length;
        const expiredLists = reportResult.expired_level_lists || {};
        actualExpired = (expiredLists.A1 || []).length + (expiredLists.A2 || []).length + (expiredLists.A3 || []).length;
        const expiringSoonLists = reportResult.expiring_soon_lists || {};
        const expiringSoon = (expiringSoonLists.L1 || []).length + (expiringSoonLists.L2 || []).length + (expiringSoonLists.L3 || []).length;
        actualTotal = actualCompleted + actualExpired + actualOpen + expiringSoon + (reportResult.no_expiry_list || []).length;
      } else {
        // For other reports, count from the lists
        actualCompleted = (reportResult.completed_list || []).length;
        actualExpired = (reportResult.expired_list || []).length;
        actualOpen = (reportResult.not_completed_list || []).length;
        actualTotal = actualCompleted + actualExpired + actualOpen;
      }
      
      // Use actual counts if we have filter applied OR for zeitraum report, otherwise use summary
      const hasFilter = (courseFilterMode !== 'all' && selectedCourses.length > 0) || (teamFilterMode !== 'all' && selectedTeams.length > 0);
      const useActualCounts = (hasFilter && actualTotal > 0) || reportResult.report_type === 'zeitraum';
      const displayCompleted = useActualCounts ? actualCompleted : (summary.completed || summary.valid || 0);
      const displayExpired = useActualCounts ? actualExpired : (summary.expired || 0);
      const displayOpen = useActualCounts ? actualOpen : (summary.not_completed || summary.open || 0);
      const displayTotal = useActualCounts ? actualTotal : (reportResult.total_entries || summary.total_entries || 0);
      const displayCompletionRate = displayTotal > 0 ? Math.round(displayCompleted / displayTotal * 100 * 10) / 10 : 0;
      
      pdf.setFontSize(11);
      pdf.setTextColor(90, 122, 155);
      pdf.text('Gesamt Einträge:', 25, yPos);
      pdf.setTextColor(13, 36, 64);
      pdf.text(String(displayTotal), 75, yPos);
      yPos += 8;
      
      pdf.setTextColor(90, 122, 155);
      pdf.text('Kurse Gesamt:', 25, yPos);
      pdf.setTextColor(13, 36, 64);
      // Count unique courses from filtered data if filter is applied
      let courseCount = summary.total_courses || 0;
      if (hasFilter || reportResult.report_type === 'zeitraum') {
        const uniqueCourses = new Set();
        [...(reportResult.completed_list || []), ...(reportResult.not_completed_list || []), ...(reportResult.expired_list || []),
         ...(reportResult.valid_list || []), ...(reportResult.open_list || []),
         ...(reportResult.completed_in_period || []), ...(reportResult.expired_in_period || []), ...(reportResult.invited_in_period || [])].forEach(e => {
          if (e.course_name) uniqueCourses.add(e.course_name);
        });
        if (uniqueCourses.size > 0) courseCount = uniqueCourses.size;
      }
      // Show "(alle)" if no specific course was selected (all courses included)
      const courseAllText = (courseFilterMode === 'all' || selectedCourses.length === 0) ? ' (alle)' : '';
      pdf.text(`${courseCount}${courseAllText}`, 75, yPos);
      yPos += 8;
      
      pdf.setTextColor(90, 122, 155);
      pdf.text('Teams Gesamt:', 25, yPos);
      pdf.setTextColor(13, 36, 64);
      // Count teams - if teams are selected, use that count, otherwise count from data
      let teamCount = 0;
      const hasTeamFilter = teamFilterMode !== 'all' && selectedTeams.length > 0;
      
      if (hasTeamFilter) {
        // Use selected teams count when filtering by specific teams
        teamCount = selectedTeams.length;
      } else if (hasFilter || reportResult.report_type === 'zeitraum') {
        // Count unique teams from filtered data
        const uniqueTeams = new Set();
        [...(reportResult.completed_list || []), ...(reportResult.not_completed_list || []), ...(reportResult.expired_list || []),
         ...(reportResult.valid_list || []), ...(reportResult.open_list || []),
         ...(reportResult.completed_in_period || []), ...(reportResult.expired_in_period || []), ...(reportResult.invited_in_period || [])].forEach(e => {
          (e.teams || []).forEach(t => { if (t) uniqueTeams.add(t); });
        });
        teamCount = uniqueTeams.size > 0 ? uniqueTeams.size : (summary.total_teams || reportResult.teams?.length || 0);
      } else {
        teamCount = summary.total_teams || reportResult.teams?.length || 0;
      }
      // Show "(alle)" if no specific teams were selected (all teams included)
      const teamAllText = (teamFilterMode === 'all' || selectedTeams.length === 0) ? ' (alle)' : '';
      pdf.text(`${teamCount}${teamAllText}`, 75, yPos);
      yPos += 10;
      
      checkPageBreak(30);
      
      // Status Overview - Different labels for Zeitraum report
      pdf.setFontSize(12);
      pdf.setTextColor(13, 36, 64);
      if (reportResult.report_type === 'zeitraum') {
        pdf.text('Änderungen im Zeitraum:', 25, yPos);
      } else {
        pdf.text('Status Übersicht Gesamt:', 25, yPos);
      }
      yPos += 8;
      
      pdf.setFontSize(10);
      if (reportResult.report_type === 'zeitraum') {
        pdf.setTextColor(34, 197, 94);
        pdf.text(`Absolviert: ${displayCompleted}`, 30, yPos);
        pdf.setTextColor(239, 68, 68);
        pdf.text(`Abgelaufen: ${displayExpired}`, 90, yPos);
        pdf.setTextColor(59, 130, 246);
        pdf.text(`Eingeladen: ${displayOpen}`, 150, yPos);
      } else {
        pdf.setTextColor(34, 197, 94);
        pdf.text(`Gültig: ${displayCompleted}`, 30, yPos);
        pdf.setTextColor(239, 68, 68);
        pdf.text(`Abgelaufen: ${displayExpired}`, 75, yPos);
        pdf.setTextColor(249, 115, 22);
        pdf.text(`Offen: ${displayOpen}`, 130, yPos);
      }
      yPos += 8;
      
      if (displayTotal > 0 && reportResult.report_type !== 'zeitraum') {
        pdf.setTextColor(46, 94, 153);
        pdf.setFont(undefined, 'bold');
        pdf.text(`Abschlussquote: ${displayCompletionRate}%`, 30, yPos);
        pdf.setFont(undefined, 'normal');
      }
      yPos += 12;
      
      pdf.setDrawColor(200, 216, 232);
      pdf.line(20, yPos, pageWidth - 20, yPos);
      yPos += 10;
      
      // Team data if available
      if (reportResult.report_type === 'team' && reportResult.teams?.length > 0) {
        checkPageBreak(30);
        
        pdf.setFontSize(14);
        pdf.setTextColor(13, 36, 64);
        pdf.text('Status pro Team', 20, yPos);
        yPos += 10;
        
        // Header row - bold
        pdf.setFontSize(10);
        pdf.setFillColor(231, 240, 250);
        pdf.rect(20, yPos - 4, pageWidth - 40, 8, 'F');
        pdf.setTextColor(13, 36, 64);
        pdf.setFont(undefined, 'bold');
        pdf.text('Team', 25, yPos);
        pdf.text('Gesamt', 100, yPos);
        pdf.text('Gültig', 120, yPos);
        pdf.text('Abgelaufen', 140, yPos);
        pdf.text('Offen', 165, yPos);
        pdf.text('Quote', 180, yPos);
        pdf.setFont(undefined, 'normal');
        yPos += 8;
        
        let teams = [...(reportResult.teams || [])].sort((a, b) => (b.completion_rate || 0) - (a.completion_rate || 0));
        
        // Filter to only selected teams if team filter is active
        if (teamFilterMode !== 'all' && selectedTeams.length > 0) {
          teams = teams.filter(t => selectedTeams.includes(t.team));
        }
        
        for (const team of teams) {
          checkPageBreak(8);
          
          // Larger font for team rows
          pdf.setFontSize(9);
          pdf.setTextColor(13, 36, 64);
          
          const teamName = team.team && team.team.length > 40 ? team.team.substring(0, 40) + '...' : (team.team || 'Unbekannt');
          pdf.text(teamName, 25, yPos);
          pdf.text(String(team.total || 0), 100, yPos);
          
          pdf.setTextColor(34, 197, 94);
          pdf.text(String(team.completed || 0), 120, yPos);
          
          pdf.setTextColor(239, 68, 68);
          pdf.text(String(team.expired || 0), 140, yPos);
          
          pdf.setTextColor(249, 115, 22);
          pdf.text(String(team.not_completed || 0), 165, yPos);
          
          const rate = team.completion_rate || 0;
          if (rate >= 80) pdf.setTextColor(34, 197, 94);
          else if (rate >= 50) pdf.setTextColor(245, 158, 11);
          else pdf.setTextColor(239, 68, 68);
          pdf.setFont(undefined, 'bold');
          pdf.text(`${rate}%`, 180, yPos);
          pdf.setFont(undefined, 'normal');
          
          yPos += 7;
        }
      }
      
      // Gesamt Report - Detailliste nach Abteilung
      if (reportResult.report_type === 'gesamt') {
        // Gruppiere nach Teams
        const teamData = {};
        
        // Helper function to find the best team for an entry
        // If team filter is active, use the first team that's in the selected teams
        // Otherwise, use the first team
        const getBestTeam = (teams) => {
          if (!teams || teams.length === 0) return 'Ohne Team';
          
          // If team filter is active, find first team that's in selectedTeams
          if (teamFilterMode !== 'all' && selectedTeams.length > 0) {
            const matchingTeam = teams.find(t => selectedTeams.includes(t));
            if (matchingTeam) return matchingTeam;
            // If no matching team found, skip this entry (will be handled by filter)
            return null;
          }
          
          return teams[0];
        };
        
        // Sammle abgelaufene Einträge nach Team
        (reportResult.expired_list || []).forEach(entry => {
          const teamName = getBestTeam(entry.teams);
          if (!teamName) return; // Skip if no matching team
          if (!teamData[teamName]) {
            teamData[teamName] = { expired: [], open: [] };
          }
          teamData[teamName].expired.push(entry);
        });
        
        // Sammle offene Einträge nach Team
        (reportResult.not_completed_list || []).forEach(entry => {
          const teamName = getBestTeam(entry.teams);
          if (!teamName) return; // Skip if no matching team
          if (!teamData[teamName]) {
            teamData[teamName] = { expired: [], open: [] };
          }
          teamData[teamName].open.push(entry);
        });
        
        // Nur Teams mit Einträgen - und nur ausgewählte Teams wenn ein Filter aktiv ist
        let teamsWithEntries = Object.keys(teamData).filter(t => 
          teamData[t].expired.length > 0 || teamData[t].open.length > 0
        ).sort();
        
        // Filter to only selected teams if team filter is active
        if (teamFilterMode !== 'all' && selectedTeams.length > 0) {
          teamsWithEntries = teamsWithEntries.filter(t => selectedTeams.includes(t));
        }
        
        if (teamsWithEntries.length > 0) {
          pdf.addPage();
          yPos = 20;
          
          pdf.setFontSize(14);
          pdf.setTextColor(13, 36, 64);
          pdf.text('Detailliste: Offen & Abgelaufen nach Abteilung', 20, yPos);
          yPos += 12;
          
          for (const teamName of teamsWithEntries) {
            const team = teamData[teamName];
            
            checkPageBreak(25);
            
            // Team Header with blue-gray background bar
            pdf.setFillColor(231, 240, 250); // Light blue-gray background
            pdf.rect(20, yPos - 6, pageWidth - 40, 12, 'F'); // Background bar
            
            pdf.setFontSize(11);
            pdf.setTextColor(13, 36, 64); // Dark blue text color for better contrast
            pdf.setFont(undefined, 'bold');
            const displayTeamName = teamName.length > 60 ? teamName.substring(0, 60) + '...' : teamName;
            pdf.text(displayTeamName, 25, yPos);
            pdf.setFont(undefined, 'normal');
            yPos += 10;
            
            // Abgelaufen
            if (team.expired.length > 0) {
              checkPageBreak(10);
              pdf.setFontSize(9);
              pdf.setTextColor(239, 68, 68);
              pdf.text(`Abgelaufen (${team.expired.length}):`, 25, yPos);
              yPos += 6;
              
              for (const entry of team.expired) {
                checkPageBreak(5);
                pdf.setFontSize(8);
                pdf.setTextColor(13, 36, 64);
                const name = entry.user_name || 'Unbekannt';
                const course = entry.course_name || '';
                const text = `• ${name} - ${course}`.substring(0, 90);
                pdf.text(text, 30, yPos);
                yPos += 4.5;
              }
              yPos += 2;
            }
            
            // Offen
            if (team.open.length > 0) {
              checkPageBreak(10);
              pdf.setFontSize(9);
              pdf.setTextColor(249, 115, 22);
              pdf.text(`Offen (${team.open.length}):`, 25, yPos);
              yPos += 6;
              
              for (const entry of team.open) {
                checkPageBreak(5);
                pdf.setFontSize(8);
                pdf.setTextColor(13, 36, 64);
                const name = entry.user_name || 'Unbekannt';
                const course = entry.course_name || '';
                const text = `• ${name} - ${course}`.substring(0, 90);
                pdf.text(text, 30, yPos);
                yPos += 4.5;
              }
              yPos += 2;
            }
            
            yPos += 4; // Space between teams
          }
        }
      }
      
      // Ablauf Report - Detailed Team View (like Gesamt report)
      if (reportResult.report_type === 'ablauf') {
        // Build team data from the level lists
        const teamData = {};
        
        // Collect all expired entries
        const allExpired = [
          ...(reportResult.expired_level_lists?.A1 || []),
          ...(reportResult.expired_level_lists?.A2 || []),
          ...(reportResult.expired_level_lists?.A3 || [])
        ];
        
        // Collect all expiring soon entries
        const allExpiringSoon = [
          ...(reportResult.expiring_soon_lists?.L1 || []),
          ...(reportResult.expiring_soon_lists?.L2 || []),
          ...(reportResult.expiring_soon_lists?.L3 || [])
        ];
        
        // Group by team
        [...allExpired, ...allExpiringSoon].forEach(entry => {
          const teams = entry.teams?.length > 0 ? entry.teams : ['Ohne Team'];
          teams.forEach(teamName => {
            if (!teamData[teamName]) {
              teamData[teamName] = { expired: [], expiring_soon: [] };
            }
            if (allExpired.includes(entry)) {
              teamData[teamName].expired.push(entry);
            } else {
              teamData[teamName].expiring_soon.push(entry);
            }
          });
        });
        
        // Get teams with entries, sorted alphabetically
        const teamsWithEntries = Object.keys(teamData)
          .filter(t => teamData[t].expired.length > 0 || teamData[t].expiring_soon.length > 0)
          .sort();
        
        if (teamsWithEntries.length > 0) {
          checkPageBreak(30);
          pdf.addPage();
          yPos = 20;
          
          pdf.setFontSize(16);
          pdf.setTextColor(13, 36, 64);
          pdf.text('Details nach Abteilung', 20, yPos);
          yPos += 15;
          
          for (const teamName of teamsWithEntries) {
            const team = teamData[teamName];
            
            checkPageBreak(25);
            
            // Team Header with blue-gray background bar
            pdf.setFillColor(231, 240, 250);
            pdf.rect(20, yPos - 6, pageWidth - 40, 12, 'F');
            
            pdf.setFontSize(11);
            pdf.setTextColor(13, 36, 64);
            pdf.setFont(undefined, 'bold');
            const displayTeamName = teamName.length > 60 ? teamName.substring(0, 60) + '...' : teamName;
            pdf.text(displayTeamName, 25, yPos);
            pdf.setFont(undefined, 'normal');
            yPos += 10;
            
            // Expired entries for this team
            if (team.expired.length > 0) {
              checkPageBreak(10);
              pdf.setFontSize(10);
              pdf.setTextColor(239, 68, 68);
              pdf.setFont(undefined, 'bold');
              pdf.text(`Abgelaufen (${team.expired.length}):`, 25, yPos);
              pdf.setFont(undefined, 'normal');
              yPos += 6;
              
              for (const entry of team.expired) {
                checkPageBreak(5);
                pdf.setFontSize(8);
                pdf.setTextColor(13, 36, 64);
                const name = entry.user_name || 'Unbekannt';
                const course = entry.course_name || '';
                const expiry = entry.expiry_date || '';
                const level = entry.expiry_level || '';
                pdf.text(`• ${name} - ${course} (${expiry}) [${level}]`, 30, yPos);
                yPos += 5;
              }
              yPos += 2;
            }
            
            // Expiring soon entries for this team
            if (team.expiring_soon.length > 0) {
              checkPageBreak(10);
              pdf.setFontSize(10);
              pdf.setTextColor(245, 158, 11);
              pdf.setFont(undefined, 'bold');
              pdf.text(`Läuft bald ab (${team.expiring_soon.length}):`, 25, yPos);
              pdf.setFont(undefined, 'normal');
              yPos += 6;
              
              for (const entry of team.expiring_soon) {
                checkPageBreak(5);
                pdf.setFontSize(8);
                pdf.setTextColor(13, 36, 64);
                const name = entry.user_name || 'Unbekannt';
                const course = entry.course_name || '';
                const expiry = entry.expiry_date || '';
                const level = entry.expiry_level || '';
                pdf.text(`• ${name} - ${course} (${expiry}) [${level}]`, 30, yPos);
                yPos += 5;
              }
              yPos += 2;
            }
            
            yPos += 4; // Space between teams
          }
        }
        
        // Summary by Level
        checkPageBreak(60);
        pdf.addPage();
        yPos = 20;
        
        pdf.setFontSize(16);
        pdf.setTextColor(13, 36, 64);
        pdf.text('Übersicht nach Ablauf-Stufen', 20, yPos);
        yPos += 15;
        
        // Expiring soon levels
        pdf.setFontSize(12);
        pdf.setTextColor(245, 158, 11);
        pdf.text('Läuft bald ab:', 20, yPos);
        yPos += 8;
        
        const expiringLevels = reportResult.summary?.expiring_soon_levels || {};
        pdf.setFontSize(10);
        pdf.setTextColor(13, 36, 64);
        pdf.text(`L1 (unter 4 Wochen): ${expiringLevels.L1 || 0}`, 25, yPos);
        yPos += 6;
        pdf.text(`L2 (4-6 Wochen): ${expiringLevels.L2 || 0}`, 25, yPos);
        yPos += 6;
        pdf.text(`L3 (6-8 Wochen): ${expiringLevels.L3 || 0}`, 25, yPos);
        yPos += 12;
        
        // Expired levels
        pdf.setFontSize(12);
        pdf.setTextColor(239, 68, 68);
        pdf.text('Abgelaufen:', 20, yPos);
        yPos += 8;
        
        const expiredLevels = reportResult.summary?.expired_levels || {};
        pdf.setFontSize(10);
        pdf.setTextColor(13, 36, 64);
        pdf.text(`A1 (bis 2 Wochen): ${expiredLevels.A1 || 0}`, 25, yPos);
        yPos += 6;
        pdf.text(`A2 (2-4 Wochen): ${expiredLevels.A2 || 0}`, 25, yPos);
        yPos += 6;
        pdf.text(`A3 (mehr als 4 Wochen): ${expiredLevels.A3 || 0}`, 25, yPos);
      }
      
      // Kurs Report - Details (no page break, continue on same page)
      if (reportResult.report_type === 'kurs' && reportResult.courses?.length > 0) {
        checkPageBreak(30);
        
        pdf.setFontSize(14);
        pdf.setTextColor(13, 36, 64);
        pdf.text('Kurs Übersicht', 20, yPos);
        yPos += 10;
        
        // Header row - bold
        pdf.setFontSize(10);
        pdf.setFillColor(231, 240, 250);
        pdf.rect(20, yPos - 4, pageWidth - 40, 8, 'F');
        pdf.setTextColor(13, 36, 64);
        pdf.setFont(undefined, 'bold');
        pdf.text('Kurs', 25, yPos);
        pdf.text('Gesamt', 120, yPos);
        pdf.text('Gültig', 140, yPos);
        pdf.text('Offen', 160, yPos);
        pdf.text('Quote', 180, yPos);
        pdf.setFont(undefined, 'normal');
        yPos += 8;
        
        for (const course of reportResult.courses) {
          checkPageBreak(7);
          // Larger font for course rows
          pdf.setFontSize(9);
          pdf.setTextColor(13, 36, 64);
          
          const courseName = (course.course || 'Unbekannt').substring(0, 50);
          pdf.text(courseName, 25, yPos);
          pdf.text(String(course.total || 0), 120, yPos);
          
          pdf.setTextColor(34, 197, 94);
          pdf.text(String(course.completed || 0), 140, yPos);
          
          pdf.setTextColor(249, 115, 22);
          pdf.text(String(course.not_completed || 0), 160, yPos);
          
          const rate = course.completion_rate || 0;
          if (rate >= 80) pdf.setTextColor(34, 197, 94);
          else if (rate >= 50) pdf.setTextColor(245, 158, 11);
          else pdf.setTextColor(239, 68, 68);
          pdf.setFont(undefined, 'bold');
          pdf.text(`${rate}%`, 180, yPos);
          pdf.setFont(undefined, 'normal');
          
          yPos += 6;
        }
      }
      
      // Zeitraum Report - Details
      if (reportResult.report_type === 'zeitraum') {
        // Absolviert im Zeitraum
        if (reportResult.completed_in_period?.length > 0) {
          checkPageBreak(30);
          pdf.addPage();
          yPos = 20;
          
          pdf.setFontSize(14);
          pdf.setTextColor(34, 197, 94);
          pdf.text(`Absolviert im Zeitraum (${reportResult.completed_in_period.length})`, 20, yPos);
          yPos += 10;
          
          pdf.setFontSize(8);
          pdf.setFillColor(240, 253, 244);
          pdf.rect(20, yPos - 4, pageWidth - 40, 7, 'F');
          pdf.setTextColor(90, 122, 155);
          pdf.text('Name', 25, yPos);
          pdf.text('Kurs', 80, yPos);
          pdf.text('Datum', 160, yPos);
          yPos += 7;
          
          for (const user of reportResult.completed_in_period.slice(0, 100)) {
            checkPageBreak(6);
            pdf.setFontSize(7);
            pdf.setTextColor(13, 36, 64);
            
            const name = (user.user_name || 'Unbekannt').substring(0, 25);
            const course = (user.course_name || '').substring(0, 40);
            const date = user.change_date || '';
            
            pdf.text(name, 25, yPos);
            pdf.text(course, 80, yPos);
            pdf.text(date, 160, yPos);
            yPos += 5;
          }
        }
        
        // Abgelaufen im Zeitraum
        if (reportResult.expired_in_period?.length > 0) {
          checkPageBreak(30);
          pdf.addPage();
          yPos = 20;
          
          pdf.setFontSize(14);
          pdf.setTextColor(239, 68, 68);
          pdf.text(`Abgelaufen im Zeitraum (${reportResult.expired_in_period.length})`, 20, yPos);
          yPos += 10;
          
          pdf.setFontSize(8);
          pdf.setFillColor(254, 242, 242);
          pdf.rect(20, yPos - 4, pageWidth - 40, 7, 'F');
          pdf.setTextColor(90, 122, 155);
          pdf.text('Name', 25, yPos);
          pdf.text('Kurs', 80, yPos);
          pdf.text('Datum', 160, yPos);
          yPos += 7;
          
          for (const user of reportResult.expired_in_period.slice(0, 100)) {
            checkPageBreak(6);
            pdf.setFontSize(7);
            pdf.setTextColor(13, 36, 64);
            
            const name = (user.user_name || 'Unbekannt').substring(0, 25);
            const course = (user.course_name || '').substring(0, 40);
            const date = user.change_date || '';
            
            pdf.text(name, 25, yPos);
            pdf.text(course, 80, yPos);
            pdf.text(date, 160, yPos);
            yPos += 5;
          }
        }
      }
      
      // ============================================
      // NEUE DETAILSEITEN: Gültig, Abgelaufen, Offen
      // ============================================
      // Only for Gesamt report type (not ablauf, zeitraum, etc.)
      if (reportResult.report_type === 'gesamt') {
        
        // --- SEITE: GÜLTIG ---
        if (reportResult.completed_list?.length > 0 || reportResult.valid_list?.length > 0) {
          pdf.addPage();
          yPos = 20;
          
          const validList = reportResult.valid_list || reportResult.completed_list || [];
          
          pdf.setFontSize(16);
          pdf.setTextColor(34, 197, 94); // Green
          pdf.setFont(undefined, 'bold');
          pdf.text(`Gültig - Detailliste (${validList.length})`, 20, yPos);
          pdf.setFont(undefined, 'normal');
          yPos += 12;
          
          // Table header
          pdf.setFontSize(9);
          pdf.setFillColor(220, 252, 231); // Light green background
          pdf.rect(20, yPos - 5, pageWidth - 40, 8, 'F');
          pdf.setTextColor(13, 36, 64);
          pdf.setFont(undefined, 'bold');
          pdf.text('Name', 25, yPos);
          pdf.text('E-Mail', 70, yPos);
          pdf.text('Kurs', 120, yPos);
          pdf.text('Gültig bis', 175, yPos);
          pdf.setFont(undefined, 'normal');
          yPos += 10;
          
          for (const entry of validList) {
            checkPageBreak(6);
            pdf.setFontSize(7);
            pdf.setTextColor(13, 36, 64);
            
            const name = (entry.user_name || 'Unbekannt').substring(0, 22);
            const email = (entry.user_email || '').substring(0, 25);
            const course = (entry.course_name || '').substring(0, 28);
            const expiresAt = entry.expires_at ? entry.expires_at.substring(0, 10) : '-';
            
            pdf.text(name, 25, yPos);
            pdf.text(email, 70, yPos);
            pdf.text(course, 120, yPos);
            pdf.text(expiresAt, 175, yPos);
            yPos += 5;
          }
        }
        
        // --- SEITE: ABGELAUFEN ---
        if (reportResult.expired_list?.length > 0) {
          pdf.addPage();
          yPos = 20;
          
          pdf.setFontSize(16);
          pdf.setTextColor(239, 68, 68); // Red
          pdf.setFont(undefined, 'bold');
          pdf.text(`Abgelaufen - Detailliste (${reportResult.expired_list.length})`, 20, yPos);
          pdf.setFont(undefined, 'normal');
          yPos += 12;
          
          // Table header
          pdf.setFontSize(9);
          pdf.setFillColor(254, 226, 226); // Light red background
          pdf.rect(20, yPos - 5, pageWidth - 40, 8, 'F');
          pdf.setTextColor(13, 36, 64);
          pdf.setFont(undefined, 'bold');
          pdf.text('Name', 25, yPos);
          pdf.text('E-Mail', 70, yPos);
          pdf.text('Kurs', 120, yPos);
          pdf.text('Abgelaufen am', 175, yPos);
          pdf.setFont(undefined, 'normal');
          yPos += 10;
          
          for (const entry of reportResult.expired_list) {
            checkPageBreak(6);
            pdf.setFontSize(7);
            pdf.setTextColor(13, 36, 64);
            
            const name = (entry.user_name || 'Unbekannt').substring(0, 22);
            const email = (entry.user_email || '').substring(0, 25);
            const course = (entry.course_name || '').substring(0, 28);
            const expiresAt = entry.expires_at ? entry.expires_at.substring(0, 10) : '-';
            
            pdf.text(name, 25, yPos);
            pdf.text(email, 70, yPos);
            pdf.text(course, 120, yPos);
            pdf.text(expiresAt, 175, yPos);
            yPos += 5;
          }
        }
        
        // --- SEITE: OFFEN ---
        if (reportResult.not_completed_list?.length > 0 || reportResult.open_list?.length > 0) {
          pdf.addPage();
          yPos = 20;
          
          const openList = reportResult.open_list || reportResult.not_completed_list || [];
          
          pdf.setFontSize(16);
          pdf.setTextColor(249, 115, 22); // Orange
          pdf.setFont(undefined, 'bold');
          pdf.text(`Offen - Detailliste (${openList.length})`, 20, yPos);
          pdf.setFont(undefined, 'normal');
          yPos += 12;
          
          // Table header
          pdf.setFontSize(9);
          pdf.setFillColor(255, 237, 213); // Light orange background
          pdf.rect(20, yPos - 5, pageWidth - 40, 8, 'F');
          pdf.setTextColor(13, 36, 64);
          pdf.setFont(undefined, 'bold');
          pdf.text('Name', 25, yPos);
          pdf.text('E-Mail', 70, yPos);
          pdf.text('Kurs', 120, yPos);
          pdf.text('Eingeladen am', 175, yPos);
          pdf.setFont(undefined, 'normal');
          yPos += 10;
          
          for (const entry of openList) {
            checkPageBreak(6);
            pdf.setFontSize(7);
            pdf.setTextColor(13, 36, 64);
            
            const name = (entry.user_name || 'Unbekannt').substring(0, 22);
            const email = (entry.user_email || '').substring(0, 25);
            const course = (entry.course_name || '').substring(0, 28);
            const invitedAt = entry.invited_at ? entry.invited_at.substring(0, 10) : '-';
            
            pdf.text(name, 25, yPos);
            pdf.text(email, 70, yPos);
            pdf.text(course, 120, yPos);
            pdf.text(invitedAt, 175, yPos);
            yPos += 5;
          }
        }
      }
      
      // Save PDF
      const reportTypeName = reportResult.report_type || 'report';
      const filename = `report_${reportTypeName}_${new Date().toISOString().slice(0,10)}.pdf`;
      
      // Use blob download method for better browser compatibility
      const pdfBlob = pdf.output('blob');
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success('PDF erstellt');
    } catch (error) {
      console.error('PDF error:', error);
      toast.error(`Fehler beim PDF-Export: ${error.message || 'Unbekannter Fehler'}`);
    }
  };

  // CSV Download handler
  const handleDownloadCSV = async () => {
    if (!reportResult) return;
    
    try {
      const response = await axios.post(
        `${API}/services/reports/download`,
        reportResult,
        { 
          responseType: 'blob',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = `report_${reportResult.report_type}_${new Date().toISOString().slice(0,10)}.csv`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('CSV-Download gestartet');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Fehler beim Download');
    }
  };

  const expirySettings = { expiryL1, expiryL2, expiryL3, expiredA1, expiredA2 };

  return (
    <Layout>
      {/* Loading Dialog */}
      <Dialog open={isGenerating}>
        <DialogContent className="sm:max-w-md" style={{ background: 'white' }}>
          <DialogHeader>
            <DialogTitle style={{ color: colors.dark }}>Report wird erstellt</DialogTitle>
            <DialogDescription style={{ color: colors.gray }}>
              Dies kann einige Minuten dauern, bitte warten...
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center py-6">
            <Loader2 className="h-10 w-10 animate-spin" style={{ color: colors.blue }} />
          </div>
        </DialogContent>
      </Dialog>

      {/* Team Selection Dialog */}
      <TeamSelectionDialog 
        open={showTeamDialog}
        onOpenChange={setShowTeamDialog}
        teams={analysisResult?.teams || []}
        selectedTeams={selectedTeams}
        setSelectedTeams={setSelectedTeams}
        searchTerm={teamDialogSearch}
        setSearchTerm={setTeamDialogSearch}
      />

      {/* Course Selection Dialog */}
      <CourseSelectionDialog 
        open={showCourseDialog}
        onOpenChange={setShowCourseDialog}
        courses={analysisResult?.courses || []}
        selectedCourses={selectedCourses}
        setSelectedCourses={setSelectedCourses}
        searchTerm={courseDialogSearch}
        setSearchTerm={setCourseDialogSearch}
      />

      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => reportResult ? backToOptions() : navigate('/dashboard')} 
            data-testid="back-button"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div 
            className={reportResult ? "cursor-pointer" : ""}
            onClick={() => reportResult && backToOptions()}
          >
            <h1 className="text-3xl font-bold" style={{ color: colors.dark }}>Reports</h1>
            <p className="mt-1" style={{ color: colors.gray }}>
              Auswertungen über Kursabsolvierungen
            </p>
          </div>
        </div>

        {!reportResult ? (
          <ReportOptionsView 
            isDataLoaded={isDataLoaded}
            isAnalyzing={isAnalyzing}
            dataStatus={dataStatus}
            analysisResult={analysisResult}
            handlePrepareReports={handlePrepareReports}
            reportType={reportType}
            setReportType={setReportType}
            filterHelperTeams={filterHelperTeams}
            setFilterHelperTeams={setFilterHelperTeams}
            courseFilterMode={courseFilterMode}
            setCourseFilterMode={setCourseFilterMode}
            selectedCourses={selectedCourses}
            setShowCourseDialog={setShowCourseDialog}
            coursePrefix={coursePrefix}
            setCoursePrefix={setCoursePrefix}
            courseExcludePrefix={courseExcludePrefix}
            setCourseExcludePrefix={setCourseExcludePrefix}
            teamFilterMode={teamFilterMode}
            setTeamFilterMode={setTeamFilterMode}
            selectedTeams={selectedTeams}
            setShowTeamDialog={setShowTeamDialog}
            teamPrefix={teamPrefix}
            setTeamPrefix={setTeamPrefix}
            teamExcludePrefix={teamExcludePrefix}
            setTeamExcludePrefix={setTeamExcludePrefix}
            expirySettings={expirySettings}
            setExpiryL1={setExpiryL1}
            setExpiryL2={setExpiryL2}
            setExpiryL3={setExpiryL3}
            setExpiredA1={setExpiredA1}
            setExpiredA2={setExpiredA2}
            referenceDate={referenceDate}
            setReferenceDate={setReferenceDate}
            periodFromDate={periodFromDate}
            setPeriodFromDate={setPeriodFromDate}
            periodToDate={periodToDate}
            setPeriodToDate={setPeriodToDate}
            exclusionList={exclusionList}
            ignoreExclusionList={ignoreExclusionList}
            setIgnoreExclusionList={setIgnoreExclusionList}
            handleGenerateReport={handleGenerateReport}
            isGenerating={isGenerating}
          />
        ) : (
          <ReportResultsView 
            reportResult={reportResult}
            selectedTeams={selectedTeams}
            selectedCourses={selectedCourses}
            courseFilterMode={courseFilterMode}
            excludedCount={excludedCount}
            expirySettings={expirySettings}
            activeExpiryFilter={activeExpiryFilter}
            setActiveExpiryFilter={setActiveExpiryFilter}
            filteredByExpiryLevel={filteredByExpiryLevel}
            filteredLists={filteredLists}
            userSearch={userSearch}
            setUserSearch={setUserSearch}
            handleUserClick={handleUserClick}
            handleDownloadCSV={handleDownloadCSV}
            handleDownloadPDF={handleDownloadPDF}
            backToOptions={backToOptions}
            resetAll={resetAll}
            resultsTopRef={resultsTopRef}
          />
        )}

        {/* User Detail Dialog */}
        <UserDetailDialog 
          open={userDetailOpen}
          onOpenChange={setUserDetailOpen}
          selectedUser={selectedUser}
        />
      </div>
    </Layout>
  );
};

// Team Selection Dialog Component
const TeamSelectionDialog = ({ 
  open, 
  onOpenChange, 
  teams, 
  selectedTeams, 
  setSelectedTeams,
  searchTerm,
  setSearchTerm 
}) => {
  const [excludeFilter, setExcludeFilter] = useState('');
  
  // Filter teams: include search term, exclude by prefix
  const getFilteredTeams = () => {
    let filtered = teams;
    
    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(t => t.toLowerCase().includes(searchTerm.toLowerCase()));
    }
    
    // Apply exclude filter (comma-separated prefixes)
    if (excludeFilter.trim()) {
      const excludePrefixes = excludeFilter.split(',').map(p => p.trim().toLowerCase()).filter(p => p);
      if (excludePrefixes.length > 0) {
        filtered = filtered.filter(t => 
          !excludePrefixes.some(prefix => t.toLowerCase().startsWith(prefix))
        );
      }
    }
    
    return filtered;
  };
  
  const filteredTeams = getFilteredTeams();
  
  return (
  <Dialog open={open} onOpenChange={(isOpen) => {
    if (!isOpen) setExcludeFilter(''); // Reset on close
    onOpenChange(isOpen);
  }}>
    <DialogContent className="sm:max-w-2xl max-h-[80vh]" style={{ background: 'white' }}>
      <DialogHeader>
        <DialogTitle style={{ color: colors.dark }}>Teams auswählen</DialogTitle>
        <DialogDescription style={{ color: colors.gray }}>
          Wählen Sie die Teams für den Report aus
        </DialogDescription>
      </DialogHeader>
      
      <div className="grid grid-cols-2 gap-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: colors.gray }} />
          <input
            type="text"
            placeholder="Team suchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-lg border text-sm"
            style={{ borderColor: colors.border, color: colors.dark }}
          />
        </div>
        <div className="relative">
          <X className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: '#ef4444' }} />
          <input
            type="text"
            placeholder="Ausschließen: Team, x_ ..."
            value={excludeFilter}
            onChange={(e) => setExcludeFilter(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-lg border text-sm"
            style={{ borderColor: colors.border, color: colors.dark }}
          />
        </div>
      </div>
      
      <div className="flex gap-2 py-2 flex-wrap">
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => setSelectedTeams(filteredTeams)}
        >
          Alle sichtbaren auswählen
        </Button>
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => setSelectedTeams([])}
          className="text-red-500"
        >
          Auswahl löschen
        </Button>
        <div className="flex-1 text-right text-sm" style={{ color: colors.gray }}>
          {selectedTeams.length} von {teams.length} ausgewählt | {filteredTeams.length} sichtbar
        </div>
      </div>
      
      <ScrollArea className="h-[400px] border rounded-lg p-3" style={{ borderColor: colors.border }}>
        <div className="space-y-1">
          {filteredTeams.map((team) => (
              <label 
                key={team} 
                className="flex items-center gap-3 p-2 cursor-pointer hover:bg-gray-50 rounded-lg border border-transparent hover:border-gray-200"
              >
                <input
                  type="checkbox"
                  checked={selectedTeams.includes(team)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedTeams([...selectedTeams, team]);
                    } else {
                      setSelectedTeams(selectedTeams.filter(t => t !== team));
                    }
                  }}
                  className="rounded h-4 w-4"
                />
                <span className="text-sm" style={{ color: colors.dark }}>{team}</span>
              </label>
            ))}
          {filteredTeams.length === 0 && (
            <p className="text-center py-8" style={{ color: colors.gray }}>
              Keine Teams gefunden
            </p>
          )}
        </div>
      </ScrollArea>
      
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={() => onOpenChange(false)}>
          Abbrechen
        </Button>
        <Button 
          onClick={() => onOpenChange(false)}
          style={{ background: colors.blue }}
        >
          Übernehmen ({selectedTeams.length})
        </Button>
      </div>
    </DialogContent>
  </Dialog>
  );
};

// Course Selection Dialog Component
const CourseSelectionDialog = ({ 
  open, 
  onOpenChange, 
  courses, 
  selectedCourses, 
  setSelectedCourses,
  searchTerm,
  setSearchTerm 
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-2xl max-h-[80vh]" style={{ background: 'white' }}>
      <DialogHeader>
        <DialogTitle style={{ color: colors.dark }}>Kurse auswählen</DialogTitle>
        <DialogDescription style={{ color: colors.gray }}>
          Wählen Sie die Kurse für den Report aus
        </DialogDescription>
      </DialogHeader>
      
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: colors.gray }} />
        <input
          type="text"
          placeholder="Kurs suchen..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 rounded-lg border text-sm"
          style={{ borderColor: colors.border, color: colors.dark }}
        />
      </div>
      
      <div className="flex gap-2 py-2">
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => {
            const filtered = searchTerm 
              ? courses.filter(c => c.toLowerCase().includes(searchTerm.toLowerCase()))
              : courses;
            setSelectedCourses(filtered);
          }}
        >
          Alle sichtbaren auswählen
        </Button>
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => setSelectedCourses([])}
          className="text-red-500"
        >
          Auswahl löschen
        </Button>
        <div className="flex-1 text-right text-sm" style={{ color: colors.gray }}>
          {selectedCourses.length} von {courses.length} ausgewählt
        </div>
      </div>
      
      <ScrollArea className="h-[400px] border rounded-lg p-3" style={{ borderColor: colors.border }}>
        <div className="space-y-1">
          {courses
            .filter(course => !searchTerm || course.toLowerCase().includes(searchTerm.toLowerCase()))
            .map((course) => (
              <label 
                key={course} 
                className="flex items-center gap-3 p-2 cursor-pointer hover:bg-gray-50 rounded-lg border border-transparent hover:border-gray-200"
              >
                <input
                  type="checkbox"
                  checked={selectedCourses.includes(course)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedCourses([...selectedCourses, course]);
                    } else {
                      setSelectedCourses(selectedCourses.filter(c => c !== course));
                    }
                  }}
                  className="rounded h-4 w-4"
                />
                <span className="text-sm" style={{ color: colors.dark }}>{course}</span>
              </label>
            ))}
          {courses.filter(course => !searchTerm || course.toLowerCase().includes(searchTerm.toLowerCase())).length === 0 && (
            <p className="text-center py-8" style={{ color: colors.gray }}>
              Keine Kurse gefunden
            </p>
          )}
        </div>
      </ScrollArea>
      
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={() => onOpenChange(false)}>
          Abbrechen
        </Button>
        <Button 
          onClick={() => onOpenChange(false)}
          style={{ background: colors.blue }}
        >
          Übernehmen ({selectedCourses.length})
        </Button>
      </div>
    </DialogContent>
  </Dialog>
);

// Report Options View Component
const ReportOptionsView = ({
  isDataLoaded,
  isAnalyzing,
  dataStatus,
  analysisResult,
  handlePrepareReports,
  reportType,
  setReportType,
  filterHelperTeams,
  setFilterHelperTeams,
  courseFilterMode,
  setCourseFilterMode,
  selectedCourses,
  setShowCourseDialog,
  coursePrefix,
  setCoursePrefix,
  courseExcludePrefix,
  setCourseExcludePrefix,
  teamFilterMode,
  setTeamFilterMode,
  selectedTeams,
  setShowTeamDialog,
  teamPrefix,
  setTeamPrefix,
  teamExcludePrefix,
  setTeamExcludePrefix,
  expirySettings,
  setExpiryL1,
  setExpiryL2,
  setExpiryL3,
  setExpiredA1,
  setExpiredA2,
  referenceDate,
  setReferenceDate,
  periodFromDate,
  setPeriodFromDate,
  periodToDate,
  setPeriodToDate,
  exclusionList,
  ignoreExclusionList,
  setIgnoreExclusionList,
  handleGenerateReport,
  isGenerating
}) => (
  <div className="grid gap-6">
    {/* Data Status Card */}
    <Card className="bg-white" style={{ borderColor: colors.border }}>
      <CardHeader>
        <CardTitle className="text-lg" style={{ color: colors.dark }}>Datenstand</CardTitle>
        <CardDescription style={{ color: colors.gray }}>
          Reports basieren auf dem geladenen eMatrixsys-Datenstand
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!isDataLoaded ? (
          <div className="text-center p-8 border-2 border-dashed rounded-lg" style={{ borderColor: colors.border }}>
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-amber-500" />
            <p className="font-medium" style={{ color: colors.dark }}>Kein Datenstand geladen</p>
            <p className="text-sm mt-2" style={{ color: colors.gray }}>
              Bitte laden Sie zuerst einen Datenstand über die Sidebar.
            </p>
          </div>
        ) : isAnalyzing ? (
          <div className="text-center p-8">
            <Loader2 className="h-12 w-12 mx-auto mb-4 animate-spin" style={{ color: colors.blue }} />
            <p className="font-medium" style={{ color: colors.dark }}>Daten werden vorbereitet...</p>
          </div>
        ) : (
          <div className="flex items-center gap-4 p-4 rounded-lg" style={{ background: colors.bg }}>
            <Database className="h-10 w-10" style={{ color: colors.blue }} />
            <div className="flex-1">
              <p className="font-medium" style={{ color: colors.dark }}>
                {dataStatus?.system_name || 'Datenstand'}
              </p>
              <p className="text-sm" style={{ color: colors.gray }}>
                {dataStatus?.accounts_count?.toLocaleString()} Accounts • {dataStatus?.periods_count?.toLocaleString()} Periods
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={handlePrepareReports} disabled={isAnalyzing}>
              <RefreshCw className={`h-4 w-4 mr-1 ${isAnalyzing ? 'animate-spin' : ''}`} />
              Neu laden
            </Button>
          </div>
        )}
      </CardContent>
    </Card>

    {analysisResult && (
      <>
        {/* Analysis Result Card */}
        <Card className="bg-white" style={{ borderColor: colors.border }}>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2" style={{ color: colors.dark }}>
              <CheckCircle className="h-5 w-5 text-green-500" />
              Daten bereit
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-3 rounded-lg" style={{ background: colors.bg }}>
                <p className="text-2xl font-bold" style={{ color: colors.dark }}>{analysisResult.row_count}</p>
                <p className="text-sm" style={{ color: colors.gray }}>Einträge</p>
              </div>
              <div className="text-center p-3 rounded-lg" style={{ background: colors.bg }}>
                <p className="text-2xl font-bold" style={{ color: colors.dark }}>{analysisResult.courses?.length || 0}</p>
                <p className="text-sm" style={{ color: colors.gray }}>Kurse</p>
              </div>
              <div className="text-center p-3 rounded-lg" style={{ background: colors.bg }}>
                <p className="text-2xl font-bold" style={{ color: colors.dark }}>{analysisResult.teams?.length || 0}</p>
                <p className="text-sm" style={{ color: colors.gray }}>Teams</p>
              </div>
              <div className="text-center p-3 rounded-lg" style={{ background: colors.bg }}>
                <p className="text-2xl font-bold text-amber-600">{analysisResult.helper_teams?.length || 0}</p>
                <p className="text-sm" style={{ color: colors.gray }}>Hilfsteams</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Report Options Card */}
        <Card className="bg-white" style={{ borderColor: colors.border }}>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2" style={{ color: colors.dark }}>
              <Filter className="h-5 w-5" style={{ color: colors.blue }} />
              Report-Optionen
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Report Type */}
            <div className="space-y-2">
              <Label>Report-Typ</Label>
              <Select value={reportType} onValueChange={setReportType}>
                <SelectTrigger data-testid="report-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="gesamt">
                    <div className="flex items-center gap-2">
                      <BarChart3 className="h-4 w-4" />
                      Gesamt-Report
                    </div>
                  </SelectItem>
                  <SelectItem value="team">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Nach Team/Abteilung
                    </div>
                  </SelectItem>
                  <SelectItem value="kurs">
                    <div className="flex items-center gap-2">
                      <BookOpen className="h-4 w-4" />
                      Nach Kurs
                    </div>
                  </SelectItem>
                  <SelectItem value="ablauf">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Ablauf-Report
                    </div>
                  </SelectItem>
                  <SelectItem value="zeitraum">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Zeitraum-Report (Änderungen)
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Reference Date (Stichtag) */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Calendar className="h-4 w-4" style={{ color: colors.blue }} />
                Stichtag (optional)
              </Label>
              <input
                type="date"
                value={referenceDate}
                onChange={(e) => setReferenceDate(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border text-sm"
                style={{ borderColor: colors.border, color: colors.dark }}
                data-testid="reference-date-input"
              />
              <p className="text-xs" style={{ color: colors.gray }}>
                Datum für Status-Berechnung (Standard: heute)
              </p>
            </div>

            {/* Period Date Range for Zeitraum Report */}
            {reportType === 'zeitraum' && (
              <div className="space-y-2 p-3 rounded-lg border" style={{ borderColor: colors.blue, background: '#f0f7ff' }}>
                <Label className="flex items-center gap-2 font-medium" style={{ color: colors.dark }}>
                  <Calendar className="h-4 w-4" style={{ color: colors.blue }} />
                  Zeitraum für Änderungen
                </Label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs block mb-1" style={{ color: colors.gray }}>Von</label>
                    <input
                      type="date"
                      value={periodFromDate}
                      onChange={(e) => setPeriodFromDate(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border text-sm"
                      style={{ borderColor: colors.border, color: colors.dark }}
                      data-testid="period-from-date-input"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-xs block mb-1" style={{ color: colors.gray }}>Bis</label>
                    <input
                      type="date"
                      value={periodToDate}
                      onChange={(e) => setPeriodToDate(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border text-sm"
                      style={{ borderColor: colors.border, color: colors.dark }}
                      data-testid="period-to-date-input"
                      required
                    />
                  </div>
                </div>
                <p className="text-xs" style={{ color: colors.gray }}>
                  Zeigt alle Änderungen (Absolvierungen, Abläufe, neue Einladungen) in diesem Zeitraum
                </p>
              </div>
            )}

            {/* Filter Helper Teams */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Filter className="h-4 w-4" style={{ color: colors.blue }} />
                Hilfsteams Filter
              </Label>
              <Button
                variant="outline"
                onClick={() => setFilterHelperTeams(!filterHelperTeams)}
                data-testid="filter-helper-teams"
                className="w-full justify-center font-medium"
                style={{
                  background: filterHelperTeams ? '#fee2e2' : '#dcfce7',
                  borderColor: filterHelperTeams ? '#ef4444' : '#22c55e',
                  color: filterHelperTeams ? '#dc2626' : '#16a34a'
                }}
              >
                {filterHelperTeams ? 'Hilfsteams ausgeblendet' : 'Hilfsteams eingeblendet'}
              </Button>
            </div>

            {/* Course Filter */}
            {analysisResult.courses?.length > 0 && (
              <div className="space-y-3">
                <Label>Kurs filtern (optional)</Label>
                <div className="flex gap-2">
                  <Button 
                    variant={courseFilterMode === 'all' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setCourseFilterMode('all')}
                    style={courseFilterMode === 'all' ? { background: colors.blue } : {}}
                  >
                    Alle
                  </Button>
                  <Button 
                    variant={courseFilterMode === 'selected' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => {
                      setCourseFilterMode('selected');
                      setShowCourseDialog(true);
                    }}
                    style={courseFilterMode === 'selected' ? { background: colors.blue } : {}}
                  >
                    Auswählen {selectedCourses.length > 0 && `(${selectedCourses.length})`}
                  </Button>
                  <Button 
                    variant={courseFilterMode === 'prefix' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setCourseFilterMode('prefix')}
                    style={courseFilterMode === 'prefix' ? { background: colors.blue } : {}}
                  >
                    Präfix
                  </Button>
                </div>
                
                {courseFilterMode === 'selected' && (
                  <div className="space-y-2">
                    <Button
                      variant="outline"
                      className="w-full justify-between"
                      onClick={() => setShowCourseDialog(true)}
                      style={{ borderColor: colors.border }}
                    >
                      <span style={{ color: colors.dark }}>
                        {selectedCourses.length > 0 ? `${selectedCourses.length} Kurs(e) ausgewählt` : 'Kurse auswählen...'}
                      </span>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                    {selectedCourses.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {selectedCourses.slice(0, 3).map(course => (
                          <Badge key={course} variant="secondary" className="text-xs">
                            {course.length > 25 ? course.substring(0, 25) + '...' : course}
                          </Badge>
                        ))}
                        {selectedCourses.length > 3 && (
                          <Badge variant="secondary" className="text-xs">+{selectedCourses.length - 3} weitere</Badge>
                        )}
                      </div>
                    )}
                  </div>
                )}
                
                {courseFilterMode === 'prefix' && (
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label className="text-xs">Einschließen (Präfixe)</Label>
                      <input
                        type="text"
                        placeholder="z.B. Schulung, IT (mehrere mit Komma trennen)"
                        value={coursePrefix}
                        onChange={(e) => setCoursePrefix(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border text-sm"
                        style={{ borderColor: colors.border, color: colors.dark }}
                      />
                      <p className="text-xs" style={{ color: colors.gray }}>
                        Zeigt Kurse die mit diesen Präfixen beginnen
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Ausschließen (Präfixe)</Label>
                      <input
                        type="text"
                        placeholder="z.B. Test (mehrere mit Komma trennen)"
                        value={courseExcludePrefix}
                        onChange={(e) => setCourseExcludePrefix(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border text-sm"
                        style={{ borderColor: colors.border, color: colors.dark }}
                      />
                      <p className="text-xs" style={{ color: colors.gray }}>
                        Blendet Kurse aus die mit diesen Präfixen beginnen
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Team Filter */}
            {analysisResult.teams?.length > 0 && (
              <div className="space-y-3">
                <Label>Team filtern (optional)</Label>
                <div className="flex gap-2">
                  <Button 
                    variant={teamFilterMode === 'all' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setTeamFilterMode('all')}
                    style={teamFilterMode === 'all' ? { background: colors.blue } : {}}
                  >
                    Alle
                  </Button>
                  <Button 
                    variant={teamFilterMode === 'selected' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => {
                      setTeamFilterMode('selected');
                      setShowTeamDialog(true);
                    }}
                    style={teamFilterMode === 'selected' ? { background: colors.blue } : {}}
                  >
                    Auswählen {selectedTeams.length > 0 && `(${selectedTeams.length})`}
                  </Button>
                  <Button 
                    variant={teamFilterMode === 'prefix' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setTeamFilterMode('prefix')}
                    style={teamFilterMode === 'prefix' ? { background: colors.blue } : {}}
                  >
                    Präfix
                  </Button>
                </div>
                
                {teamFilterMode === 'selected' && (
                  <div className="space-y-2">
                    <Button
                      variant="outline"
                      className="w-full justify-between"
                      onClick={() => setShowTeamDialog(true)}
                      style={{ borderColor: colors.border }}
                    >
                      <span style={{ color: colors.dark }}>
                        {selectedTeams.length > 0 ? `${selectedTeams.length} Team(s) ausgewählt` : 'Teams auswählen...'}
                      </span>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                    {selectedTeams.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {selectedTeams.slice(0, 3).map(team => (
                          <Badge key={team} variant="secondary" className="text-xs">
                            {team.length > 20 ? team.substring(0, 20) + '...' : team}
                          </Badge>
                        ))}
                        {selectedTeams.length > 3 && (
                          <Badge variant="secondary" className="text-xs">+{selectedTeams.length - 3} weitere</Badge>
                        )}
                      </div>
                    )}
                  </div>
                )}
                
                {teamFilterMode === 'prefix' && (
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label className="text-xs">Einschließen (Präfixe)</Label>
                      <input
                        type="text"
                        placeholder="z.B. W, S (mehrere mit Komma trennen)"
                        value={teamPrefix}
                        onChange={(e) => setTeamPrefix(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border text-sm"
                        style={{ borderColor: colors.border, color: colors.dark }}
                      />
                      <p className="text-xs" style={{ color: colors.gray }}>
                        Zeigt Teams die mit diesen Präfixen beginnen
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Ausschließen (Präfixe)</Label>
                      <input
                        type="text"
                        placeholder="z.B. Team (mehrere mit Komma trennen)"
                        value={teamExcludePrefix}
                        onChange={(e) => setTeamExcludePrefix(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border text-sm"
                        style={{ borderColor: colors.border, color: colors.dark }}
                      />
                      <p className="text-xs" style={{ color: colors.gray }}>
                        Blendet Teams aus die mit diesen Präfixen beginnen
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Expiry Level Settings */}
            <ExpirySettingsSection 
              expirySettings={expirySettings}
              setExpiryL1={setExpiryL1}
              setExpiryL2={setExpiryL2}
              setExpiryL3={setExpiryL3}
              setExpiredA1={setExpiredA1}
              setExpiredA2={setExpiredA2}
            />

            {/* Exclusion List Toggle */}
            {exclusionList.length > 0 && (
              <div className="flex items-center justify-between pt-4 border-t" style={{ borderColor: colors.border }}>
                <div>
                  <Label className="flex items-center gap-2">
                    <UserX className="h-4 w-4" style={{ color: ignoreExclusionList ? colors.gray : '#ef4444' }} />
                    Ausschlussliste ({exclusionList.length} E-Mails)
                  </Label>
                  <p className="text-xs mt-1" style={{ color: colors.gray }}>
                    {ignoreExclusionList ? 'Alle Benutzer werden angezeigt' : 'Administratoren werden herausgefiltert'}
                  </p>
                </div>
                <Switch
                  checked={ignoreExclusionList}
                  onCheckedChange={setIgnoreExclusionList}
                  data-testid="ignore-exclusion-toggle"
                />
              </div>
            )}

            {/* Generate Button */}
            <Button
              onClick={handleGenerateReport}
              disabled={isGenerating}
              className="w-full"
              size="lg"
              style={{ background: colors.dark }}
              data-testid="generate-report-button"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Report wird erstellt...
                </>
              ) : (
                <>
                  <FileText className="mr-2 h-5 w-5" />
                  Report erstellen
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </>
    )}
  </div>
);

// Expiry Settings Section Component
const ExpirySettingsSection = ({
  expirySettings,
  setExpiryL1,
  setExpiryL2,
  setExpiryL3,
  setExpiredA1,
  setExpiredA2
}) => {
  const { expiryL1, expiryL2, expiryL3, expiredA1, expiredA2 } = expirySettings;
  
  return (
    <div className="space-y-4 pt-4 border-t" style={{ borderColor: colors.border }}>
      <Label className="flex items-center gap-2">
        <Clock className="h-4 w-4" style={{ color: colors.blue }} />
        Ablauf-Stufen konfigurieren
      </Label>
      
      <div className="space-y-2">
        <p className="text-sm font-medium" style={{ color: colors.dark }}>Läuft bald ab (L1-L3)</p>
        <div className="grid grid-cols-3 gap-2">
          {[
            { label: 'L1', value: expiryL1, setter: setExpiryL1 },
            { label: 'L2', value: expiryL2, setter: setExpiryL2 },
            { label: 'L3', value: expiryL3, setter: setExpiryL3 }
          ].map(({ label, value, setter }) => (
            <div key={label}>
              <label className="text-xs" style={{ color: colors.gray }}>{label} (Wochen)</label>
              <input
                type="number"
                min="1"
                max="52"
                value={value}
                onChange={(e) => setter(Math.max(1, parseInt(e.target.value) || value))}
                className="w-full px-2 py-1 rounded border text-sm text-center"
                style={{ borderColor: colors.border, color: colors.dark }}
              />
            </div>
          ))}
        </div>
      </div>
      
      <div className="space-y-2">
        <p className="text-sm font-medium" style={{ color: colors.dark }}>Abgelaufen (A1-A3)</p>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-xs" style={{ color: colors.gray }}>A1: bis zu (Wochen)</label>
            <input
              type="number"
              min="1"
              max="52"
              value={expiredA1}
              onChange={(e) => setExpiredA1(Math.max(1, parseInt(e.target.value) || 2))}
              className="w-full px-2 py-1 rounded border text-sm text-center"
              style={{ borderColor: colors.border, color: colors.dark }}
            />
          </div>
          <div>
            <label className="text-xs" style={{ color: colors.gray }}>A2: bis zu (Wochen)</label>
            <input
              type="number"
              min="1"
              max="52"
              value={expiredA2}
              onChange={(e) => setExpiredA2(Math.max(1, parseInt(e.target.value) || 4))}
              className="w-full px-2 py-1 rounded border text-sm text-center"
              style={{ borderColor: colors.border, color: colors.dark }}
            />
          </div>
        </div>
        <p className="text-xs" style={{ color: colors.gray }}>
          A3 = mehr als {expiredA2} Wochen abgelaufen
        </p>
      </div>
    </div>
  );
};

// Zeitraum Summary Cards Component
const ZeitraumSummaryCards = ({ summary }) => (
  <>
    <Card className="bg-white border-l-4 border-l-green-500" style={{ borderColor: colors.border }}>
      <CardContent className="pt-4">
        <div className="text-center">
          <TrendingUp className="h-6 w-6 mx-auto text-green-500 mb-1" />
          <div className="text-2xl font-bold text-green-600">
            {summary?.changes?.completed_in_period || 0}
          </div>
          <div className="text-xs" style={{ color: colors.gray }}>Absolviert</div>
        </div>
      </CardContent>
    </Card>
    <Card className="bg-white border-l-4 border-l-red-500" style={{ borderColor: colors.border }}>
      <CardContent className="pt-4">
        <div className="text-center">
          <TrendingDown className="h-6 w-6 mx-auto text-red-500 mb-1" />
          <div className="text-2xl font-bold text-red-600">
            {summary?.changes?.expired_in_period || 0}
          </div>
          <div className="text-xs" style={{ color: colors.gray }}>Abgelaufen</div>
        </div>
      </CardContent>
    </Card>
    <Card className="bg-white border-l-4 border-l-blue-500" style={{ borderColor: colors.border }}>
      <CardContent className="pt-4">
        <div className="text-center">
          <UserPlus className="h-6 w-6 mx-auto text-blue-500 mb-1" />
          <div className="text-2xl font-bold text-blue-600">
            {summary?.changes?.invited_in_period || 0}
          </div>
          <div className="text-xs" style={{ color: colors.gray }}>Eingeladen</div>
        </div>
      </CardContent>
    </Card>
    <Card className="bg-white border-l-4 border-l-purple-500" style={{ borderColor: colors.border }}>
      <CardContent className="pt-4">
        <div className="text-center">
          <BarChart3 className="h-6 w-6 mx-auto text-purple-500 mb-1" />
          <div className="text-2xl font-bold text-purple-600">
            {summary?.changes?.total_changes || 0}
          </div>
          <div className="text-xs" style={{ color: colors.gray }}>Änderungen gesamt</div>
        </div>
      </CardContent>
    </Card>
  </>
);

// Zeitraum Report Content Component
const ZeitraumReportContent = ({ reportResult, handleUserClick }) => {
  const [activeTab, setActiveTab] = useState('completed');
  
  const tabs = [
    { id: 'completed', label: 'Absolviert', count: reportResult.completed_in_period?.length || 0, color: 'green' },
    { id: 'expired', label: 'Abgelaufen', count: reportResult.expired_in_period?.length || 0, color: 'red' },
    { id: 'invited', label: 'Eingeladen', count: reportResult.invited_in_period?.length || 0, color: 'blue' },
    { id: 'teams', label: 'Nach Team', count: reportResult.teams?.length || 0, color: 'purple' }
  ];
  
  const getList = () => {
    switch(activeTab) {
      case 'completed': return reportResult.completed_in_period || [];
      case 'expired': return reportResult.expired_in_period || [];
      case 'invited': return reportResult.invited_in_period || [];
      default: return [];
    }
  };
  
  return (
    <Card className="bg-white" style={{ borderColor: colors.border }}>
      <CardHeader>
        <div className="flex gap-2 flex-wrap">
          {tabs.map(tab => (
            <Button
              key={tab.id}
              size="sm"
              variant={activeTab === tab.id ? 'default' : 'outline'}
              onClick={() => setActiveTab(tab.id)}
              className={activeTab === tab.id ? `bg-${tab.color}-500` : ''}
              style={activeTab === tab.id ? { 
                background: tab.color === 'green' ? '#22c55e' : 
                           tab.color === 'red' ? '#ef4444' : 
                           tab.color === 'blue' ? '#3b82f6' : '#8b5cf6' 
              } : {}}
            >
              {tab.label} ({tab.count})
            </Button>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        {activeTab === 'teams' ? (
          <ZeitraumTeamsList teams={reportResult.teams} handleUserClick={handleUserClick} />
        ) : (
          <ZeitraumChangesList 
            items={getList()} 
            changeType={activeTab}
            handleUserClick={handleUserClick} 
          />
        )}
      </CardContent>
    </Card>
  );
};

// List of changes for Zeitraum report
const ZeitraumChangesList = ({ items, changeType, handleUserClick }) => {
  const getIcon = () => {
    switch(changeType) {
      case 'completed': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'expired': return <Clock className="h-4 w-4 text-red-500" />;
      case 'invited': return <UserPlus className="h-4 w-4 text-blue-500" />;
      default: return null;
    }
  };
  
  const getColorClass = () => {
    switch(changeType) {
      case 'completed': return 'border-green-200 bg-green-50';
      case 'expired': return 'border-red-200 bg-red-50';
      case 'invited': return 'border-blue-200 bg-blue-50';
      default: return '';
    }
  };
  
  return (
    <ScrollArea className="h-[400px]">
      <div className="space-y-2">
        {items.length > 0 ? items.map((item, idx) => (
          <div 
            key={idx} 
            className={`p-3 rounded-lg border ${getColorClass()}`}
          >
            <div className="flex items-start gap-3">
              {getIcon()}
              <div className="flex-1">
                <p 
                  className="font-medium cursor-pointer hover:underline"
                  style={{ color: colors.dark }}
                  onClick={() => handleUserClick(item)}
                >
                  {item.user_name || item.user_email || 'Unbekannt'}
                </p>
                <div className="flex flex-wrap items-center gap-3 text-xs mt-1" style={{ color: colors.gray }}>
                  {item.course_name && (
                    <span className="flex items-center gap-1">
                      <BookOpen className="h-3 w-3" />
                      {item.course_name}
                    </span>
                  )}
                  {item.team && (
                    <span className="flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      {item.team}
                    </span>
                  )}
                  {item.change_date && (
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {item.change_date}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )) : (
          <p className="text-center py-8" style={{ color: colors.gray }}>
            Keine Änderungen in diesem Zeitraum
          </p>
        )}
      </div>
    </ScrollArea>
  );
};

// Teams list for Zeitraum report
const ZeitraumTeamsList = ({ teams, handleUserClick }) => (
  <ScrollArea className="h-[400px]">
    <div className="space-y-3">
      {teams && teams.length > 0 ? teams.map((team, idx) => (
        <div key={idx} className="p-3 rounded-lg border" style={{ borderColor: colors.border }}>
          <div className="flex items-center justify-between mb-2">
            <p className="font-medium" style={{ color: colors.dark }}>{team.team}</p>
            <Badge variant="secondary">{team.total_changes} Änderungen</Badge>
          </div>
          <div className="grid grid-cols-3 gap-2 text-sm">
            <div className="p-2 rounded bg-green-50 text-center">
              <p className="font-bold text-green-600">{team.completed_count}</p>
              <p className="text-xs text-green-700">Absolviert</p>
            </div>
            <div className="p-2 rounded bg-red-50 text-center">
              <p className="font-bold text-red-600">{team.expired_count}</p>
              <p className="text-xs text-red-700">Abgelaufen</p>
            </div>
            <div className="p-2 rounded bg-blue-50 text-center">
              <p className="font-bold text-blue-600">{team.invited_count}</p>
              <p className="text-xs text-blue-700">Eingeladen</p>
            </div>
          </div>
        </div>
      )) : (
        <p className="text-center py-8" style={{ color: colors.gray }}>
          Keine Team-Änderungen in diesem Zeitraum
        </p>
      )}
    </div>
  </ScrollArea>
);

// Report Results View Component
const ReportResultsView = ({
  reportResult,
  selectedTeams,
  selectedCourses,
  courseFilterMode,
  excludedCount,
  expirySettings,
  activeExpiryFilter,
  setActiveExpiryFilter,
  filteredByExpiryLevel,
  filteredLists,
  userSearch,
  setUserSearch,
  handleUserClick,
  handleDownloadCSV,
  handleDownloadPDF,
  backToOptions,
  resetAll,
  resultsTopRef
}) => (
  <div className="space-y-6">
    {/* Scroll anchor */}
    <div ref={resultsTopRef} />
    
    {/* Report Type Header */}
    <div className="flex items-center justify-between">
      <h2 className="text-xl font-semibold" style={{ color: colors.dark }}>
        {reportResult.report_type === 'gesamt' && 'Gesamt - Report'}
        {reportResult.report_type === 'team' && 'Team - Report'}
        {reportResult.report_type === 'kurs' && 'Kurs - Report'}
        {reportResult.report_type === 'ablauf' && 'Ablauf - Report'}
        {reportResult.report_type === 'zeitraum' && 'Zeitraum - Report'}
        {selectedTeams.length > 0 && ` (${selectedTeams.length} Teams)`}
        {selectedCourses.length > 0 && ` (${selectedCourses.length} Kurse)`}
      </h2>
      <div className="text-right">
        <p className="text-sm" style={{ color: colors.gray }}>
          {reportResult.summary?.total_entries || 0} Einträge
        </p>
        {reportResult.reference_date && (
          <p className="text-xs" style={{ color: colors.gray }}>
            Stichtag: {reportResult.reference_date}
          </p>
        )}
      </div>
    </div>

    {/* Period Info for Zeitraum Report */}
    {reportResult.report_type === 'zeitraum' && reportResult.period && (
      <div className="p-4 rounded-lg" style={{ background: '#f0f7ff', border: '1px solid #2E5E99' }}>
        <div className="flex items-center gap-2 mb-2">
          <Calendar className="h-5 w-5" style={{ color: colors.blue }} />
          <p className="font-medium" style={{ color: colors.dark }}>
            Zeitraum: {reportResult.period.from} bis {reportResult.period.to}
          </p>
          <Badge variant="secondary">{reportResult.summary?.period_days} Tage</Badge>
        </div>
      </div>
    )}

    {/* Selected Teams Display */}
    {selectedTeams.length > 0 && (
      <div className="p-3 rounded-lg" style={{ background: colors.bg, borderColor: colors.border, border: '1px solid' }}>
        <p className="text-sm font-medium mb-2" style={{ color: colors.dark }}>
          Ausgewählte Teams ({selectedTeams.length}):
        </p>
        <div className="flex flex-wrap gap-2">
          {selectedTeams.map((team, idx) => (
            <Badge key={idx} variant="secondary" className="text-xs">{team}</Badge>
          ))}
        </div>
      </div>
    )}

    {/* Selected Courses Display */}
    {selectedCourses.length > 0 && (
      <div className="p-3 rounded-lg" style={{ background: colors.bg, borderColor: colors.border, border: '1px solid' }}>
        <p className="text-sm font-medium mb-2" style={{ color: colors.dark }}>
          Ausgewählte Kurse ({selectedCourses.length}):
        </p>
        <div className="flex flex-wrap gap-2">
          {selectedCourses.map((course, idx) => (
            <Badge key={idx} variant="secondary" className="text-xs">{course.length > 30 ? course.substring(0, 30) + '...' : course}</Badge>
          ))}
        </div>
      </div>
    )}

    {/* Summary Cards */}
    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
      {reportResult.report_type === 'gesamt' && (
        <GesamtSummaryCards summary={reportResult.summary} excludedCount={excludedCount} />
      )}
      {reportResult.report_type === 'ablauf' && (
        <AblaufSummaryCards summary={reportResult.summary} />
      )}
      {reportResult.report_type === 'zeitraum' && (
        <ZeitraumSummaryCards summary={reportResult.summary} />
      )}
    </div>

    {/* Expiry Level Filter - only for Ablauf report (stays above charts) */}
    {reportResult.report_type === 'ablauf' && (
      <ExpiryLevelFilterCard
        reportResult={reportResult}
        expirySettings={expirySettings}
        activeExpiryFilter={activeExpiryFilter}
        setActiveExpiryFilter={setActiveExpiryFilter}
      />
    )}

    {/* Charts Section */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="charts-container">
      {reportResult.report_type === 'gesamt' && (
        <>
          <StatusPieChart summary={reportResult.summary} />
          <StatusBarChart summary={reportResult.summary} />
        </>
      )}
      
      {reportResult.report_type === 'ablauf' && (
        <>
          <AblaufPieChart summary={reportResult.summary} activeFilter={activeExpiryFilter} />
          <AblaufBarChart summary={reportResult.summary} activeFilter={activeExpiryFilter} />
        </>
      )}

      {/* Filtered Results by Expiry Level - only for non-ablauf reports */}
      {reportResult.report_type !== 'zeitraum' && reportResult.report_type !== 'ablauf' && (
        <FilteredExpiryList
          activeExpiryFilter={activeExpiryFilter}
          filteredByExpiryLevel={filteredByExpiryLevel}
          expirySettings={expirySettings}
          onUserClick={handleUserClick}
          onClearFilter={() => setActiveExpiryFilter(null)}
        />
      )}

      {/* Team Report Table */}
      {reportResult.report_type === 'team' && (
        <TeamReportTable teams={reportResult.teams} onUserClick={handleUserClick} />
      )}

      {/* Course Report Chart */}
      {reportResult.report_type === 'kurs' && (
        <CourseBarChart courses={reportResult.courses} />
      )}
    </div>

    {/* Expiry Level Filter - below charts for all reports except Ablauf and Zeitraum */}
    {reportResult.report_type !== 'zeitraum' && reportResult.report_type !== 'ablauf' && (
      <ExpiryLevelFilterCard
        reportResult={reportResult}
        expirySettings={expirySettings}
        activeExpiryFilter={activeExpiryFilter}
        setActiveExpiryFilter={setActiveExpiryFilter}
      />
    )}

    {/* Zeitraum Report Content */}
    {reportResult.report_type === 'zeitraum' && (
      <ZeitraumReportContent reportResult={reportResult} handleUserClick={handleUserClick} />
    )}

    {/* Detail Tabs / Content */}
    {reportResult.report_type !== 'zeitraum' && (
      <Card className="bg-white" style={{ borderColor: colors.border }}>
        {reportResult.report_type === 'gesamt' && (
          <GesamtReportTabs
            reportResult={reportResult}
            filteredLists={filteredLists}
            userSearch={userSearch}
            setUserSearch={setUserSearch}
            onUserClick={handleUserClick}
          />
        )}

        {reportResult.report_type === 'kurs' && (
          <KursReportSection courses={reportResult.courses} onUserClick={handleUserClick} />
        )}

        {reportResult.report_type === 'ablauf' && (
          <AblaufReportTabs 
            reportResult={reportResult} 
            userSearch={userSearch}
            setUserSearch={setUserSearch}
            onUserClick={handleUserClick}
            activeExpiryFilter={activeExpiryFilter}
            setActiveExpiryFilter={setActiveExpiryFilter}
          />
        )}
      </Card>
    )}

    {/* Action Buttons */}
    <ReportActions
      onDownloadCSV={handleDownloadCSV}
      onDownloadPDF={handleDownloadPDF}
      onBackToOptions={backToOptions}
      onResetAll={resetAll}
    />
  </div>
);

export default Reports;
